import { useEffect, useMemo, useState } from 'react'
import { Form, Button, Card } from 'react-bootstrap'
import { useNavigate } from 'react-router-dom'
import toast from 'react-hot-toast'
import isEmail from 'validator/lib/isEmail'

import {
  isValidName,
  isValidAddress,
  isValidPostcode,
  isValidCountry,
  isValidDob,
  isValidCityOrCounty,
  isValidPhoneNumber,
  isValidAccountType,
} from '../../../utils/validateInput'
import ControlInput from '../../../components/form-inputs/SignupControlInput'
import SignupSelectInput from '../../../components/form-inputs/SignupSelectInput'
import { useCompleteMemberDetailsMutation } from '../../../app/userApiSlice'
import useAuth from '../../../hooks/useAuth'
import BackButtonHeader from '../../../components/buttons/BackButtonHeader'
import calculateIsUnder16 from '../../../utils/calculateIsUnder16'
import LoadingTextSwap from '../../../components/LoadingTextSwap'

const SignupForm = ({ membership, existingAccountType = '' }) => {
  const navigate = useNavigate()
  const { uid, role } = useAuth()

  const [completeMemberDetails, { isLoading }] = useCompleteMemberDetailsMutation()

  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [dob, setDob] = useState('')
  const [streetAddress, setStreetAddress] = useState('')
  const [city, setCity] = useState('')
  const [county, setCounty] = useState('')
  const [postcode, setPostcode] = useState('')
  const [country, setCountry] = useState('')
  const [phoneNumber, setPhoneNumber] = useState('')
  const [accountType, setAccountType] = useState(existingAccountType)
  const [newsletter, setNewsletter] = useState(false)
  const [clubNumber, setClubNumber] = useState('')
  const [parentFirstName, setParentFirstName] = useState('')
  const [parentLastName, setParentLastName] = useState('')
  const [parentPhoneNumber, setParentPhoneNumber] = useState('')
  const [parentEmail, setParentEmail] = useState('')

  const [error, setError] = useState('')
  const [clubNumberError, setClubNumberError] = useState('')
  const [blurAll, setBlurAll] = useState(false)

  const [valid, setValid] = useState({
    firstName: false,
    lastName: false,
    dob: false,
    streetAddress: false,
    city: false,
    county: false,
    postcode: false,
    country: false,
    phoneNumber: false,
    accountType: existingAccountType ? true : false,
  })

  const [parentValid, setParentValid] = useState({
    parentFirstName: false,
    parentLastName: false,
    parentPhoneNumber: false,
    parentEmail: false,
  })

  // Function to update valid state
  const updateValid = (key, value) => {
    setValid(prev => ({ ...prev, [key]: value }))
  }

  // Function to update parent valid state
  const updateParentValid = (key, value) => {
    setParentValid(prev => ({ ...prev, [key]: value }))
  }

  // Check if user is under 16 and memoize to avoid recalculating on every render
  const isUnder16 = useMemo(() => calculateIsUnder16(dob), [dob])

  // Parent data is only required to be valid if user is under 16
  const parentDataValid = isUnder16 ? Object.values(parentValid).every(Boolean) : true // If 16+, parent data doesn't matter

  const allValid = Object.values(valid).every(Boolean) && parentDataValid

  const handleClubNumChange = e => {
    setClubNumber(e.target.value)
    setClubNumberError('')
  }

  // Clear error message when certain fields are updated
  useEffect(() => {
    if (error && allValid) {
      setError('')
    }
  }, [allValid])

  const handleSubmit = async e => {
    e.preventDefault()

    if (error || isLoading) return

    if (!allValid) {
      setBlurAll(true)
      return setError('All fields must be valid')
    }

    try {
      await completeMemberDetails({
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        dob,
        address: {
          streetAddress: streetAddress.replace(/\n/g, ', ').trim(),
          city: city.trim(),
          county: county.trim(),
          postcode: postcode.toUpperCase().trim(),
          country: country.trim(),
        },
        phoneNumber: phoneNumber.trim(),
        accountType,
        newsletter,
        clubNumber,
        uid,
        parentFirstName: parentFirstName.trim(),
        parentLastName: parentLastName.trim(),
        parentPhoneNumber: parentPhoneNumber.trim(),
        parentEmail: parentEmail.trim(),
      }).unwrap()

      if (!membership || membership.initialPaymentComplete) {
        navigate('/user')
      } else {
        navigate('/payments')
      }
    } catch (error) {
      if (error?.data?.message === 'Club does not exist') {
        setClubNumberError('Club could not be found. Please check club number and try again.')
      } else {
        toast.error('An error occurred. Please try again.')
      }
    }
  }

  return (
    <>
      {role === 'admin' ? (
        <BackButtonHeader page="Create User" />
      ) : (
        <>
          <h1 className="text-center mt-sm-3 fw-semibold">Welcome to Gymnastic Britannia</h1>
          <p className="mb-4 mt-4 text-center fw-semibold fs-5">Please complete this form to proceed</p>
        </>
      )}
      <Card className="mb-5 p-3 w-100 border-0 shadow text-start">
        <Card.Body>
          <Form onSubmit={handleSubmit}>
            <h4 className="my-3 fw-bold">Personal Details</h4>

            <div className="d-flex justify-content-between gap-3">
              <ControlInput
                label="First Name"
                type="text"
                name="firstName"
                value={firstName}
                isValid={valid.firstName}
                setValid={updateValid}
                setData={setFirstName}
                validTest={isValidName}
                blurAll={blurAll}
                feedback="Please enter a valid first name"
              />
              <ControlInput
                label="Last Name"
                type="text"
                name="lastName"
                value={lastName}
                isValid={valid.lastName}
                setValid={updateValid}
                setData={setLastName}
                validTest={isValidName}
                blurAll={blurAll}
                feedback="Please enter a valid last name"
              />
            </div>

            <div className="d-flex justify-content-between gap-3">
              <ControlInput
                label="Date of Birth"
                type="date"
                name="dob"
                value={dob}
                isValid={valid.dob}
                setValid={updateValid}
                setData={setDob}
                validTest={isValidDob}
                blurAll={blurAll}
                feedback="Please enter a valid date of birth"
              />
              <ControlInput
                label="Phone Number"
                type="tel"
                name="phoneNumber"
                value={phoneNumber}
                isValid={valid.phoneNumber}
                setValid={updateValid}
                setData={setPhoneNumber}
                validTest={isValidPhoneNumber}
                blurAll={blurAll}
                feedback="Please enter a valid phone number"
              />
            </div>

            {/* Parent/Guardian Details */}
            {isUnder16 && valid.dob && (
              <>
                <hr className="mt-5" style={{ border: '1px solid #ccc' }} />
                <h4 className="my-3 fw-bold">Parent/Guardian Details</h4>

                <div className="d-flex justify-content-between gap-3">
                  <ControlInput
                    label="Parent/Guardian First Name"
                    type="text"
                    name="parentFirstName"
                    value={parentFirstName}
                    isValid={parentValid.parentFirstName}
                    setValid={updateParentValid}
                    setData={setParentFirstName}
                    validTest={isValidName}
                    blurAll={blurAll}
                    feedback="Please enter a valid first name"
                  />
                  <ControlInput
                    label="Parent/Guardian last Name"
                    type="text"
                    name="parentLastName"
                    value={parentLastName}
                    isValid={parentValid.parentLastName}
                    setValid={updateParentValid}
                    setData={setParentLastName}
                    validTest={isValidName}
                    blurAll={blurAll}
                    feedback="Please enter a valid last name"
                  />
                </div>
                <div className="d-flex justify-content-between gap-3">
                  <ControlInput
                    label="Parent/Guardian Email"
                    type="email"
                    name="parentEmail"
                    value={parentEmail}
                    isValid={parentValid.parentEmail}
                    setValid={updateParentValid}
                    setData={setParentEmail}
                    validTest={isEmail}
                    blurAll={blurAll}
                    feedback="Please enter a valid email"
                  />
                  <ControlInput
                    label="Parent/Guardian Phone Number"
                    type="tel"
                    name="parentPhoneNumber"
                    value={parentPhoneNumber}
                    isValid={parentValid.parentPhoneNumber}
                    setValid={updateParentValid}
                    setData={setParentPhoneNumber}
                    validTest={isValidPhoneNumber}
                    blurAll={blurAll}
                    feedback="Please enter a valid phone number"
                  />
                </div>
              </>
            )}

            <hr className="mt-5" style={{ border: '1px solid #ccc' }} />
            <h4 className="my-3 fw-bold">Address</h4>

            <ControlInput
              type="text"
              name="streetAddress"
              value={streetAddress}
              isValid={valid.streetAddress}
              setValid={updateValid}
              setData={setStreetAddress}
              validTest={isValidAddress}
              blurAll={blurAll}
              as="textarea"
              rows={3}
              feedback="Please enter a valid street address"
              label="Street Address"
            />

            <div className="d-flex justify-content-between gap-3">
              <ControlInput
                type="text"
                name="city"
                value={city}
                isValid={valid.city}
                setValid={updateValid}
                setData={setCity}
                validTest={isValidCityOrCounty}
                blurAll={blurAll}
                label="Town/City"
                feedback="Please enter a valid town or city"
              />
              <ControlInput
                type="text"
                name="county"
                value={county}
                isValid={valid.county}
                setValid={updateValid}
                setData={setCounty}
                validTest={isValidCityOrCounty}
                blurAll={blurAll}
                label="County/State"
                feedback="Please enter a valid county or state"
              />
            </div>

            <div className="d-flex justify-content-between gap-3">
              <ControlInput
                type="text"
                name="postcode"
                value={postcode}
                isValid={valid.postcode}
                setValid={updateValid}
                setData={setPostcode}
                validTest={isValidPostcode}
                blurAll={blurAll}
                label="Postcode"
                feedback="Please enter a valid postcode"
              />
              <ControlInput
                type="text"
                name="country"
                value={country}
                isValid={valid.country}
                setValid={updateValid}
                setData={setCountry}
                validTest={isValidCountry}
                blurAll={blurAll}
                label="Country"
                feedback="Please enter a valid country"
              />
            </div>

            <hr className="mt-5" style={{ border: '1px solid #ccc' }} />

            <h4 className="my-3 fw-bold">Account</h4>

            <SignupSelectInput
              existingAccountType={existingAccountType}
              label="Account Type"
              name="accountType"
              isValid={valid.accountType}
              setValid={updateValid}
              setData={setAccountType}
              validTest={isValidAccountType}
              blurAll={blurAll}
              feedback="Please select an account type"
            />

            <Form.Group className="mb-2 w-100" controlId="clubNumber">
              <Form.Label className="mt-2  fw-semibold text-muted">Club Number (optional)</Form.Label>
              <Form.Control onChange={handleClubNumChange} isInvalid={clubNumberError} />
              <Form.Control.Feedback type="invalid" className="fw-semibold">
                <i>{clubNumberError}</i>
              </Form.Control.Feedback>
            </Form.Group>

            <Form.Group className=" mt-4 mb-3 text-center" controlId="newsletter">
              <Form.Check
                type="checkbox"
                label="Sign up to our newsletter"
                checked={newsletter}
                onChange={e => setNewsletter(e.target.checked)}
                className="d-inline-block fw-semibold text-muted"
              />
            </Form.Group>

            <Form.Group className="mt-4">
              <p className="px-3 text-muted">
                By clicking "Sign Up", you agree to Gymnastic Britannia's{' '}
                <a
                  href="https://www.gymnasticbritannia.co.uk/_files/ugd/a71aa4_9f49f4545fa5449a905c00d3d2f7eaaa.pdf?fbclid=IwY2xjawIsDjJleHRuA2FlbQIxMAABHUtR3MJUH4m7mnFM5hZitiD-GyPV9L7dncfZjvCVOiApPShXK-PK5r0sVw_aem_TNMw5t2SQNc5la4ZMg2bEg"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  rules and terms of service
                </a>{' '}
                and{' '}
                <a href="https://www.gymnasticbritannia.co.uk/privacy" target="_blank" rel="noopener noreferrer">
                  GDPR policy
                </a>
              </p>
            </Form.Group>

            {error && <p className="text-danger i text-center fw-semibold">{error}</p>}

            <Button disabled={isLoading} className="w-100 mt-1 rounded-3" variant="dark" type="submit">
              <LoadingTextSwap isLoading={isLoading}>Submit</LoadingTextSwap>
            </Button>
          </Form>
        </Card.Body>
      </Card>
    </>
  )
}

export default SignupForm
